import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customerOrders, supplierOrders,
  type CustomerOrderItem, type SupplierOrderItem,
  type OrderKind,
} from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { goodsSales } from '@/api/goodsSales';
import { goodsReceipts } from '@/api/goodsReceipts';
import { taxInvoicesIncoming, taxInvoicesOutgoing } from '@/api/taxInvoices';
import { bankPrefill, linkBankDoc, manualLink, paymentNote } from '@/shared/utils/reverseChain';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import { ProjectField } from '@/components/ProjectSelect';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { fmtDocDate, moneyDocActions, vatTail } from '@/shared/utils/paymentFromDoc';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import NomenclatureAvailabilityPicker from '@/components/NomenclatureAvailabilityPicker';
import FixedAssetPickerModal from '@/components/FixedAssetPickerModal';
import { assetsRegistry } from '@/api/assetsRegistry';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import { reports, type GoodsBalanceRow } from '@/api/documents';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useContractPriceLookup, useMainContractAutoSelect } from '@/hooks/useContractDefaults';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';
import VatFlagsButton from '@/shared/components/VatFlagsButton';
import {
  DEFAULT_VAT_FLAGS, lineAmounts, orgVatDefaults, recalcRows, type DocVatFlags, vatPayerOn,
} from '@/shared/utils/vatFlags';

type Kind = 'customer' | 'supplier';

interface ItemRow {
  line_number: number;
  nomenclature_id: string;
  /** 0445: рядок-ОЗ (продаж основного засобу) — nomenclature_id порожній. */
  fixed_asset_id?: string | null;
  quantity: string;
  quantity_shipped?: string;
  quantity_received?: string;
  quantity_reserved?: string;
  price: string;
  discount_percent?: string;
  service_description?: string;  // Порція А: «Зміст послуги» (друк рахунку)
  /** 0388: склад рядка (null = склад шапки) — резерв по-складово. */
  warehouse_id?: string | null;
  /** Локальна підказка з підбору (вкладка «Послуги»): якщо номенклатури
   *  нема у завантаженому довіднику (limit) — рядок все одно лишається
   *  на правильній вкладці. У payload НЕ йде. */
  is_service_hint?: boolean;
  vat_rate_id: string;
  /** 2026-07-31 (вкладка «Обладнання»): рахунок рядка 15х — переноситься
   *  у прихід/реалізацію при «створити на підставі». */
  account_code?: string | null;
  amount_without_vat: string;
  vat_amount: string;
  total_amount: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  delivery_date: string;
  delivery_days: string;  // 0381: «N днів з моменту оплати» ('' = режим дати)
  prepayment_due_date: string;  // 0382: планова дата ЧАСТКОВОЇ передоплати
  reserve_until: string;  // 0389: «Резерв до» (авто: оплата + 1 день)
  comment: string;
  project_id: string | null;
  order_kind: OrderKind;
  items: ItemRow[];
  /** ч.1374: ознаки ПДВ документа як у 1С (умовчання — з картки організації). */
  vat_flags: DocVatFlags;
  // ПРОДАЖ-розбивка Етап 1 (2026-06-03).
  incoterms: string;
  payment_terms_type: '' | 'PREPAY' | 'PARTIAL' | 'POSTPAY' | 'ON_DELIVERY';
  prepayment_percent: string;
  final_payment_days: string;
  // Порція А (0379): поля УТП.
  payment_due_date: string;
  bank_account_id: string;
  base_order_id: string;
  delivery_address: string;
  consignee_name: string;
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  delivery_date: '',
  delivery_days: '',
  prepayment_due_date: '',
  reserve_until: '',
  comment: '',
  project_id: null,
  order_kind: 'ORDER',
  items: [],
  vat_flags: { ...DEFAULT_VAT_FLAGS },
  incoterms: '',
  payment_terms_type: '',
  prepayment_percent: '',
  final_payment_days: '',
  payment_due_date: '',
  bank_account_id: '',
  base_order_id: '',
  delivery_address: '',
  consignee_name: '',
};

const INCOTERMS_OPTIONS = [
  'EXW', 'FCA', 'FAS', 'FOB',
  'CFR', 'CIF', 'CPT', 'CIP',
  'DAP', 'DPU', 'DDP',
] as const;

const PAYMENT_TERMS_LABELS: Record<string, string> = {
  PREPAY: 'Повна передоплата',
  PARTIAL: 'Часткова передоплата',
  POSTPAY: 'Постоплата',
  ON_DELIVERY: 'При отриманні',
};

const newRow = (n: number, vatId: string): ItemRow => ({
  line_number: n, nomenclature_id: '', quantity: '0', price: '0',
  discount_percent: '0',
  vat_rate_id: vatId, amount_without_vat: '0', vat_amount: '0', total_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ч.851: іконка документа для кнопок «створити …» (блоки 2 і 4). */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function OrderEditor({ kind }: { kind: Kind }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const isCustomer = kind === 'customer';
  // ПРОДАЖ-розбивка Етап 1 (2026-06-03): URL ?kind=PROFORMA_INVOICE з invoices сторінки.
  const urlKind = searchParams.get('kind') as OrderKind | null;
  // baseRoute визначається з URL pathname або URL kind param.
  const isInvoiceContext = urlKind === 'PROFORMA_INVOICE'
    || window.location.pathname.includes('-invoices');
  const baseRoute = isInvoiceContext
    ? (isCustomer ? '/customer-invoices' : '/supplier-invoices')
    : (isCustomer ? '/customer-orders' : '/supplier-orders');
  const partyLabel = isCustomer ? 'Покупець' : 'Постачальник';
  // 2026-07-24 (рішення власника): замовлення = «хто/що/скільки/коли».
  // Грошові й оформлювальні поля (умови оплати, інкотермс, планова дата
  // оплати, банк-рахунок, адреса/вантажоодержувач) — лише на РАХУНКУ
  // (див. invoiceFields нижче).

  const queryKeySingle = isCustomer ? 'customer-order' : 'supplier-order';
  const queryKeyList = isCustomer ? 'customer-orders' : 'supplier-orders';

  const api = isCustomer ? customerOrders : supplierOrders;

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по per-kind permission
  const requiredPerm = isCustomer ? 'sales.order.manage' : 'purchases.order.manage';
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg(requiredPerm);
  const contractorsList = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  // 2026-07-25 (рішення власника, як в 1С): НЕ фільтруємо жорстко за
  // роллю — показуємо ВСІХ контрагентів (ролеві першими). Роль
  // проставляється автоматично при першому документі (backend).
  const filteredContractors = useMemo(() => {
    if (!contractorsList.data) return [];
    const items = [...contractorsList.data.items];
    items.sort((a, b) => {
      const ra = isCustomer ? (a.is_buyer ? 0 : 1) : (a.is_supplier ? 0 : 1);
      const rb = isCustomer ? (b.is_buyer ? 0 : 1) : (b.is_supplier ? 0 : 1);
      return ra - rb || a.name.localeCompare(b.name, 'uk');
    });
    return items;
  }, [contractorsList.data, isCustomer]);
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: [queryKeySingle, id],
    queryFn: () => api.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, queryKeySingle, (cid) => api.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);
  // 2026-07-25: ховаємо колонки ПДВ якщо org НЕ платник (як у Реалізації).
  // EXEMPT-ставка автоматично у нових рядках → суми без ПДВ.
  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  // ч.1374: колонки ПДВ — платник І «враховувати ПДВ» у документі.
  // ч.1383: платник ПДВ — на дату документа; збережений документ з ПДВ лишається з ПДВ,
  // навіть якщо організація потім перестала бути платником.
  const docVatPayer = vatPayerOn(currentOrg, form.document_date)
    || (!isNew && form.vat_flags.vat_considered);
  const showVatColumns = docVatPayer && form.vat_flags.vat_considered;
  const rateOf = (vatRateId: string | null | undefined) =>
    vatList.data?.find((v) => v.id === vatRateId)?.rate_percent ?? 0;
  // Ознаки нового документа — з картки організації, поки користувач їх не змінив сам.
  const vatFlagsTouched = useRef(false);
  useEffect(() => {
    if (!isNew || copy.source || vatFlagsTouched.current || !currentOrg) return;
    const next = orgVatDefaults(currentOrg, form.document_date);
    setForm((f) => (f.vat_flags.vat_considered === next.vat_considered
      && f.vat_flags.prices_include_vat === next.prices_include_vat && f.vat_flags.vat_auto === next.vat_auto)
      ? f : { ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, copy.source, currentOrg, vatList.data, form.document_date]);
  function applyVatFlags(next: DocVatFlags) {
    vatFlagsTouched.current = true;
    setForm((f) => ({ ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf) }));
  }
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveDefaultVatId = showVatColumns ? defaultVatId : exemptVatId;
  // ч.1423: ставка ПДВ з картки при виборі позиції — як у реалізації і надходженні.
  const vatRateByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.vat_rate_id) m.set(n.id, n.vat_rate_id);
    }
    return m;
  }, [nomList.data]);

  // Порція А (0379): банківський рахунок для друку рахунку (Б/счет УТП).
  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', form.organization_id],
    queryFn: () => refs.bankAccounts(form.organization_id),
    enabled: !!form.organization_id && isCustomer,
  });
  useEffect(() => {
    if (isNew && isCustomer && !form.bank_account_id && bankAccountsQ.data?.length) {
      const main = bankAccountsQ.data.find((b) => b.is_main) ?? bankAccountsQ.data[0];
      if (main) setForm((f) => (f.bank_account_id ? f : { ...f, bank_account_id: main.id }));
    }
  }, [isNew, isCustomer, bankAccountsQ.data, form.bank_account_id]);

  // Порція А: вкладки «Товари/Послуги» (ТЧ Услуги УТП) + попап-підбір.
  const [linesTab, setLinesTab] = useState<'goods' | 'services' | 'equipment' | 'assets'>('goods');
  // 0445: рядки-ОЗ у рахунку покупцю (вкладка «ОЗ»).
  const [faPickerOpen, setFaPickerOpen] = useState(false);
  const faListQ = useQuery({
    queryKey: ['fa-for-sale', form.organization_id],
    queryFn: () => assetsRegistry.list({ organization_id: form.organization_id, kind: 'FIXED_ASSET', limit: 500 }),
    enabled: isCustomer && !!form.organization_id,
  });
  const faById = useMemo(() => {
    const m = new Map<string, { name: string; inventory_number: string; residual_value: string }>();
    for (const a of faListQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [faListQ.data]);
  const faLabel = (id?: string | null) => {
    const a = id ? faById.get(id) : undefined;
    return a ? `${a.inventory_number} — ${a.name}` : '—';
  };
  const rowIsFa = (it: ItemRow) => !!it.fixed_asset_id;
  // 2026-07-31 (рішення власника): вкладка «Обладнання» — рядок з рахунком
  // 15х; при «створити на підставі» прихід/реалізація кладуть його на
  // свою вкладку «Обладнання» (авто-152).
  const rowIsEquip = (it: ItemRow) => !it.fixed_asset_id && (it.account_code ?? '').startsWith('15');
  const [pickerOpen, setPickerOpen] = useState(false);
  // 2026-07-31: швидке створення номенклатури з документа.
  const [quickNomOpen, setQuickNomOpen] = useState(false);

  // 2026-07-25: «створити з замовлення» на РАХУНКУ — попап активних
  // (відкритих, проведених) замовлень обраного покупця; вибір заповнює
  // дату поставки і табличну частину.
  const [ordersPickerOpen, setOrdersPickerOpen] = useState(false);
  // 2026-07-29 (ревізія Покупок): kind-aware — рахунок ВІД ПОСТАЧАЛЬНИКА
  // теж заповнюється з активного замовлення постачальнику.
  const activeOrdersQ = useQuery({
    queryKey: ['active-orders-for-invoice', kind, form.organization_id, form.contractor_id],
    queryFn: () => (isCustomer ? customerOrders : supplierOrders).list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      order_kind: 'ORDER',
      closing_status: 'OPEN',
      posted: true,
      limit: 100,
    }),
    enabled: ordersPickerOpen && !!form.contractor_id && !!form.organization_id,
  });
  // Підстава рахунку (замовлення): номер для показу + посилання (2026-07-31).
  const basisOrderQ = useQuery({
    queryKey: ['order-brief', kind, form.base_order_id],
    queryFn: () => (isCustomer ? customerOrders : supplierOrders).get(form.base_order_id),
    enabled: !!form.base_order_id,
  });
  async function applyFromOrder(orderId: string) {
    try {
      const src = await (isCustomer ? customerOrders : supplierOrders).get(orderId);
      setForm((f) => ({
        ...f,
        delivery_date: src.delivery_date ?? '',
        delivery_days: src.delivery_days != null ? String(src.delivery_days) : '',
        // «При отриманні»: планова дата оплати слідує за терміном поставки.
        payment_due_date: f.payment_terms_type === 'ON_DELIVERY'
          ? (src.delivery_date ?? '') : f.payment_due_date,
        base_order_id: orderId,
        warehouse_id: f.warehouse_id || (src.warehouse_id ?? ''),
        items: src.items.map((it, idx) => ({
          line_number: idx + 1,
          nomenclature_id: it.nomenclature_id ?? '',
          quantity: String(it.quantity),
          price: String(it.price),
          discount_percent: 'discount_percent' in it && (it as ItemRow).discount_percent != null
            ? String((it as ItemRow).discount_percent) : '0',
          service_description: (it as { service_description?: string }).service_description ?? '',
          warehouse_id: (it as { warehouse_id?: string | null }).warehouse_id ?? null,
          account_code: (it as { account_code?: string | null }).account_code ?? null,
          fixed_asset_id: (it as { fixed_asset_id?: string | null }).fixed_asset_id ?? null,
          vat_rate_id: it.vat_rate_id,
          amount_without_vat: String(it.amount_without_vat),
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
        })),
      }));
      setOrdersPickerOpen(false);
    } catch (e) {
      setError(extractError(e));
    }
  }
  const isServiceByNomId = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const n of nomList.data?.items ?? []) m.set(n.id, !!n.is_service);
    return m;
  }, [nomList.data]);
  const rowIsService = (it: ItemRow) =>
    it.nomenclature_id
      ? (isServiceByNomId.get(it.nomenclature_id) ?? it.is_service_hint ?? false)
      : false;
  const goodsCount = form.items.filter((it) => it.nomenclature_id && !rowIsService(it) && !rowIsEquip(it)).length;
  const servicesCount = form.items.filter((it) => rowIsService(it)).length;
  const equipCount = form.items.filter((it) => it.nomenclature_id && rowIsEquip(it) && !rowIsService(it)).length;
  const faCount = form.items.filter(rowIsFa).length;
  // Видимі рядки. На вкладці «Товари» порожні рядки НЕ показуємо — замість
  // них кнопка «Додати» (підбір). На вкладці «Послуги» порожній рядок
  // лишається (послуги вводяться вручну, у підборі залишків їх немає).
  const visibleRows = form.items
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => {
      if (linesTab === 'assets') return rowIsFa(it);
      if (rowIsFa(it)) return false;
      if (linesTab === 'equipment') return !!it.nomenclature_id && rowIsEquip(it) && !rowIsService(it);
      if (!it.nomenclature_id) return linesTab === 'services';
      if (rowIsEquip(it)) return false;
      return linesTab === 'services' ? rowIsService(it) : !rowIsService(it);
    });

  // ч.750 (рішення власника): «Разом» — по вкладці, «Усього» — по документу.
  const tabWithoutVat = visibleRows.reduce((s, { it }) => s + Number(it.amount_without_vat || 0), 0);
  const tabVat = visibleRows.reduce((s, { it }) => s + Number(it.vat_amount || 0), 0);

  // opts (ч.923) передає лише попап підбору покупця: price — ціна, по якій
  // клікнули (null — рядок без ціни); без opts — як раніше, ціна за договором.
  function addRowFromPicker(picked: GoodsBalanceRow, opts?: { price: string | null }) {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        ...newRow(f.items.length + 1, showVatColumns
          ? (vatRateByNomId.get(picked.nomenclature_id) ?? effectiveDefaultVatId)
          : effectiveDefaultVatId),
        nomenclature_id: picked.nomenclature_id,
        is_service_hint: linesTab === 'services',
        account_code: linesTab === 'equipment' ? '152' : null,
        // ч.661: джерело істини — склад рядка; підбір пише явний склад,
        // шапка — лише дефолт.
        warehouse_id: picked.warehouse_id || f.warehouse_id || null,
        discount_percent: (isCustomer && contractDiscount) ? contractDiscount : '0',
        ...(opts?.price ? { price: opts.price } : {}),
      }],
    }));
    if (opts) { setPickerOpen(false); return; }
    void priceFor(picked.nomenclature_id).then((pr) => {
      if (pr == null) return;
      setForm((f) => ({
        ...f,
        items: f.items.map((row) =>
          row.nomenclature_id === picked.nomenclature_id && Number(row.price) === 0
            ? { ...row, price: pr } : row),
      }));
    });
    setPickerOpen(false);
  }

  // П1 (2026-07-23): УТП-дефолти договору — автовибір основного (is_main)
  // + ціна номенклатури за типом цін договору.
  useMainContractAutoSelect({
    // 2026-07-25: і при редагуванні DRAFT — зміна контрагента скидає договір,
    // основний нового контрагента підставляється (posted-док не зачіпається:
    // у нього contract_id завжди заповнений — хук мовчить).
    enabled: true,
    contractorId: form.contractor_id,
    contractId: form.contract_id,
    contracts: contractsQ.data,
    onSelect: (cid) => setForm((f) => (f.contract_id
      ? f
      // Автопідбір основного договору теж підставляє його умови оплати.
      : { ...f, contract_id: cid, ...termsFromContract(cid, f) })),
  });
  const { priceFor } = useContractPriceLookup(
    contractsQ.data, form.contract_id, form.organization_id, form.document_date);
  // П2: дефолтний % знижки за договором — у нові рядки замовлення покупця.
  const contractDiscount = useMemo(() => {
    const c = (contractsQ.data ?? []).find((x) => x.id === form.contract_id);
    const d = Number(c?.discount_percent ?? 0);
    return d > 0 ? String(d) : '';
  }, [contractsQ.data, form.contract_id]);

  // ч.1352 (обернений ланцюг «на підставі»): рахунок З реалізації / надходження, які вже є
  // (?from_sale=<id> / ?from_receipt=<id>): шапка і рядки — з документа; після створення
  // документи звʼязуються (рядок у «Звʼязаних документах» з обох боків). Колонки обліку
  // (customer_order_id реалізації) не чіпаємо — рахунок пост-фактум нічого не резервує.
  const fromSaleId = isCustomer ? searchParams.get('from_sale') : null;
  const fromReceiptId = !isCustomer ? searchParams.get('from_receipt') : null;
  // ч.1359: з оплати (банк), з податкової накладної і з рахунку (→ замовлення).
  const bankReceiptId = isCustomer ? searchParams.get('bank_receipt') : null;
  const bankPaymentId = !isCustomer ? searchParams.get('bank_payment') : null;
  const pnOutId = isCustomer ? searchParams.get('tax_invoice') : null;
  const pnInId = !isCustomer ? searchParams.get('tax_invoice_in') : null;
  const fromInvoiceId = searchParams.get('from_invoice');
  const ownType = isCustomer ? 'doc.customer_orders' : 'doc.supplier_orders';
  const linkSource = async (orderId: string) => {
    const src = fromSaleId
      ? { linked_type: 'doc.goods_sales', linked_id: fromSaleId }
      : fromReceiptId ? { linked_type: 'doc.goods_receipts', linked_id: fromReceiptId }
      : pnOutId ? { linked_type: 'tax.tax_invoices_outgoing', linked_id: pnOutId }
      : pnInId ? { linked_type: 'tax.tax_invoices_incoming', linked_id: pnInId }
      : fromInvoiceId ? { linked_type: ownType, linked_id: fromInvoiceId } : null;
    if (src) {
      const problem = await manualLink(ownType, orderId, src.linked_type, src.linked_id);
      if (problem) setError(problem);
    }
    const bankId = bankReceiptId ?? bankPaymentId;
    if (bankId) {
      const problem = await linkBankDoc({
        kind: bankReceiptId ? 'receipt' : 'payment', bankId, createdType: ownType, createdId: orderId,
        field: isCustomer ? 'customer_order_id' : 'supplier_order_id',
      });
      if (problem) setError(problem);
    }
    void qc.invalidateQueries({ queryKey: ['related-docs'] });
  };
  const fromDocApplied = useRef(false);
  useEffect(() => {
    if (!isNew || fromDocApplied.current || (!fromSaleId && !fromReceiptId)) return;
    fromDocApplied.current = true;
    void (async () => {
      try {
        const rows: ItemRow[] = [];
        let head: Partial<FormState> = {};
        if (fromSaleId) {
          const sale = await goodsSales.get(fromSaleId);
          head = { organization_id: sale.organization_id, contractor_id: sale.contractor_id,
            contract_id: sale.contract_id ?? '', warehouse_id: sale.warehouse_id ?? '' };
          sale.items.forEach((it, idx) => {
            if (!it.nomenclature_id) return;
            rows.push({
              line_number: idx + 1, nomenclature_id: it.nomenclature_id,
              quantity: String(it.quantity), price: String(it.price),
              discount_percent: it.discount_percent != null ? String(it.discount_percent) : '0',
              service_description: it.service_description ?? '',
              warehouse_id: it.warehouse_id ?? null, vat_rate_id: it.vat_rate_id,
              amount_without_vat: String(it.amount_without_vat), vat_amount: String(it.vat_amount),
              total_amount: String(it.total_amount),
            } as ItemRow);
          });
        } else if (fromReceiptId) {
          const rc = await goodsReceipts.get(fromReceiptId);
          head = { organization_id: rc.organization_id, contractor_id: rc.contractor_id,
            contract_id: rc.contract_id ?? '', warehouse_id: rc.warehouse_id ?? '' };
          const all = [...rc.items, ...(rc.services ?? [])];
          all.forEach((it, idx) => {
            if (!it.nomenclature_id) return;
            rows.push({
              line_number: idx + 1, nomenclature_id: it.nomenclature_id,
              quantity: String(it.quantity ?? '1'), price: String(it.price ?? '0'),
              discount_percent: '0',
              service_description: (it as { description?: string | null }).description ?? '',
              warehouse_id: null, vat_rate_id: it.vat_rate_id ?? defaultVatId,
              amount_without_vat: String(it.amount_without_vat), vat_amount: String(it.vat_amount),
              total_amount: String(it.total_amount),
            } as ItemRow);
          });
        }
        setForm((f) => ({
          ...f, ...head, document_number: '', order_kind: 'PROFORMA_INVOICE',
          payment_terms_type: (f.payment_terms_type || 'PREPAY') as FormState['payment_terms_type'],
          items: rows.length ? rows : f.items,
        }));
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, fromSaleId, fromReceiptId, defaultVatId]);
  const reverseApplied = useRef(false);
  useEffect(() => {
    const bankId = bankReceiptId ?? bankPaymentId;
    const pnId = pnOutId ?? pnInId;
    if (!isNew || reverseApplied.current || (!bankId && !pnId && !fromInvoiceId)) return;
    reverseApplied.current = true;
    const kindFromUrl: OrderKind = (urlKind === 'PROFORMA_INVOICE' || isInvoiceContext) ? 'PROFORMA_INVOICE' : 'ORDER';
    void (async () => {
      try {
        if (bankId) {
          const p = await bankPrefill(bankReceiptId ? 'receipt' : 'payment', bankId);
          setForm((f) => ({
            ...f, document_number: '', order_kind: kindFromUrl,
            organization_id: p.organization_id || f.organization_id,
            contractor_id: p.contractor_id || f.contractor_id,
            contract_id: p.contract_id || f.contract_id,
            document_date: p.document_date || f.document_date,
            payment_terms_type: (f.payment_terms_type || 'PREPAY') as FormState['payment_terms_type'],
            comment: f.comment || paymentNote(bankReceiptId ? 'receipt' : 'payment', p),
          }));
          return;
        }
        if (pnId) {
          const pn = pnOutId ? await taxInvoicesOutgoing.get(pnOutId) : await taxInvoicesIncoming.get(pnInId!);
          const rows: ItemRow[] = [];
          pn.items.filter((it) => it.nomenclature_id && Number(it.quantity) > 0).forEach((it, idx) => {
            const base = Number(it.base_amount); const vat = Number(it.vat_amount);
            rows.push({
              line_number: idx + 1, nomenclature_id: it.nomenclature_id!,
              quantity: String(Number(it.quantity)), price: String(Number(it.price_without_vat)),
              discount_percent: '0', service_description: it.description ?? '',
              warehouse_id: null, vat_rate_id: it.vat_rate_id || defaultVatId,
              amount_without_vat: base.toFixed(2), vat_amount: vat.toFixed(2), total_amount: (base + vat).toFixed(2),
            } as ItemRow);
          });
          setForm((f) => ({
            ...f, document_number: '', order_kind: kindFromUrl,
            organization_id: pn.organization_id, contractor_id: pn.contractor_id ?? f.contractor_id,
            contract_id: pn.contract_id ?? f.contract_id,
            document_date: pn.invoice_date ? `${pn.invoice_date}T12:00` : f.document_date,
            payment_terms_type: (f.payment_terms_type || 'PREPAY') as FormState['payment_terms_type'],
            items: rows.length ? rows : f.items,
          }));
          return;
        }
        if (fromInvoiceId) {
          // рахунок → замовлення: шапка і рядки рахунку; звʼязок — ручний (рядок «Звʼязаних»)
          const src = await api.get(fromInvoiceId);
          setForm((f) => ({
            ...f, document_number: '', order_kind: 'ORDER',
            organization_id: src.organization_id, contractor_id: src.contractor_id,
            contract_id: src.contract_id ?? '', warehouse_id: src.warehouse_id ?? '',
            delivery_date: src.delivery_date ?? '', project_id: src.project_id ?? null,
            comment: src.comment ?? '',
            items: src.items.map((it, idx) => ({
              line_number: idx + 1, nomenclature_id: it.nomenclature_id ?? '',
              quantity: String(it.quantity), price: String(it.price),
              discount_percent: (it as { discount_percent?: number | string | null }).discount_percent != null
                ? String((it as { discount_percent?: number | string | null }).discount_percent) : '0',
              service_description: (it as { service_description?: string | null }).service_description ?? '',
              warehouse_id: (it as { warehouse_id?: string | null }).warehouse_id ?? null,
              vat_rate_id: it.vat_rate_id,
              amount_without_vat: String(it.amount_without_vat), vat_amount: String(it.vat_amount),
              total_amount: String(it.total_amount),
            } as ItemRow)),
          }));
        }
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, bankReceiptId, bankPaymentId, pnOutId, pnInId, fromInvoiceId, defaultVatId, urlKind, isInvoiceContext]);

  // П1: «створити на підставі» — копія іншого замовлення/рахунку
  // (?copy_from=<id>; order_kind визначає URL-контекст: /-invoices → рахунок).
  const copyFromId = searchParams.get('copy_from');
  const copyApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !copyFromId || copyApplied.current) return;
    copyApplied.current = true;
    void (async () => {
      try {
        const src = await api.get(copyFromId);
        const targetKind: OrderKind = (urlKind === 'PROFORMA_INVOICE' || isInvoiceContext)
          ? 'PROFORMA_INVOICE' : 'ORDER';
        setForm((f) => ({
          ...f,
          organization_id: src.organization_id,
          document_number: '', // auto через number-схему
          contractor_id: src.contractor_id,
          contract_id: src.contract_id ?? '',
          warehouse_id: src.warehouse_id ?? '',
          delivery_date: src.delivery_date ?? '',
          delivery_days: src.delivery_days != null ? String(src.delivery_days) : '',
          prepayment_due_date: (src as { prepayment_due_date?: string | null }).prepayment_due_date ?? '',
          payment_due_date: (src as any).payment_due_date ?? '',
          delivery_address: (src as any).delivery_address ?? '',
          consignee_name: (src as any).consignee_name ?? '',
          bank_account_id: (src as any).bank_account_id ?? '',
          // Порція А: рахунок, створений із замовлення, зберігає посилання-підставу.
          base_order_id: (targetKind === 'PROFORMA_INVOICE'
            && (src.order_kind ?? 'ORDER') === 'ORDER') ? copyFromId : '',
          project_id: src.project_id ?? null,
          order_kind: targetKind,
          incoterms: src.incoterms ?? '',
          // Рахунок без умов у джерелі → дефолт «Повна передплата».
          payment_terms_type: (src.payment_terms_type
            ?? (targetKind === 'PROFORMA_INVOICE' ? 'PREPAY' : '')) as FormState['payment_terms_type'],
          prepayment_percent: src.prepayment_percent != null ? String(src.prepayment_percent) : '',
          final_payment_days: src.final_payment_days != null ? String(src.final_payment_days) : '',
          comment: src.comment ?? '',
          items: src.items.map((it, idx) => ({
            line_number: idx + 1,
            nomenclature_id: it.nomenclature_id ?? '',
            quantity: String(it.quantity),
            price: String(it.price),
            discount_percent: 'discount_percent' in it && (it as ItemRow).discount_percent != null
              ? String((it as ItemRow).discount_percent) : '0',
            service_description: (it as any).service_description ?? '',
            warehouse_id: (it as { warehouse_id?: string | null }).warehouse_id ?? null,
            vat_rate_id: it.vat_rate_id,
            amount_without_vat: String(it.amount_without_vat),
            vat_amount: String(it.vat_amount),
            total_amount: String(it.total_amount),
          })),
        }));
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, copyFromId]);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      // ПРОДАЖ-розбивка Етап 1: для нового документа беремо order_kind з URL.
      const initialKind: OrderKind = (urlKind === 'PROFORMA_INVOICE' || isInvoiceContext)
        ? 'PROFORMA_INVOICE'
        : 'ORDER';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        order_kind: initialKind,
        // 2026-07-25 (рішення власника): «Умови оплати» на новому рахунку
        // за замовчуванням — Повна передплата.
        payment_terms_type: initialKind === 'PROFORMA_INVOICE'
          ? (f.payment_terms_type || 'PREPAY') : f.payment_terms_type,
        // Порожній рядок НЕ додаємо: рядки додаються кнопкою «Додати»
        // (товари — підбір, послуги — вручну). Порожній у стейті ламав би
        // збереження (422).
        items: f.items,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        warehouse_id: d.warehouse_id ?? '',
        delivery_date: d.delivery_date ?? '',
        delivery_days: d.delivery_days != null ? String(d.delivery_days) : '',
        prepayment_due_date: d.prepayment_due_date ?? '',
        reserve_until: d.reserve_until ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        order_kind: d.order_kind ?? 'ORDER',
        vat_flags: {
          vat_considered: d.vat_considered ?? true,
          prices_include_vat: d.prices_include_vat ?? false,
          vat_auto: d.vat_auto ?? true,
        },
        incoterms: d.incoterms ?? '',
        payment_terms_type: (d.payment_terms_type ?? '') as FormState['payment_terms_type'],
        prepayment_percent: d.prepayment_percent ?? '',
        final_payment_days: d.final_payment_days !== undefined && d.final_payment_days !== null
          ? String(d.final_payment_days)
          : '',
        payment_due_date: (d as any).payment_due_date ?? '',
        bank_account_id: (d as any).bank_account_id ?? '',
        base_order_id: (d as any).base_order_id ?? '',
        delivery_address: (d as any).delivery_address ?? '',
        consignee_name: (d as any).consignee_name ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id ?? '',
          quantity: String(it.quantity),
          quantity_shipped: 'quantity_shipped' in it ? String(it.quantity_shipped ?? '0') : undefined,
          quantity_received: 'quantity_received' in it ? String(it.quantity_received ?? '0') : undefined,
          quantity_reserved: 'quantity_reserved' in it ? String(it.quantity_reserved ?? '0') : undefined,
          price: String(it.price),
          discount_percent: 'discount_percent' in it && it.discount_percent != null
            ? String(it.discount_percent) : '0',
          service_description: (it as any).service_description ?? '',
          warehouse_id: (it as { warehouse_id?: string | null }).warehouse_id ?? null,
          vat_rate_id: it.vat_rate_id,
          account_code: (it as { account_code?: string | null }).account_code ?? null,
          fixed_asset_id: (it as { fixed_asset_id?: string | null }).fixed_asset_id ?? null,
          amount_without_vat: String(it.amount_without_vat),
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), base_order_id: EMPTY_FORM.base_order_id, delivery_date: EMPTY_FORM.delivery_date, prepayment_due_date: EMPTY_FORM.prepayment_due_date, reserve_until: EMPTY_FORM.reserve_until, payment_due_date: EMPTY_FORM.payment_due_date, }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, defaultVatId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // ч.948 (рішення власника): «створити ↗» біля контрагента і договору
  // відкриває ПОПАП просто на сторінці документа (як у ПКО/РКО).
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);
  const contractorNameForQuick = filteredContractors.find((c: { id: string; name: string }) => c.id === form.contractor_id)?.name ?? '';

  // 2026-07-27 (UX як в УТП): якщо в організації РІВНО ОДИН склад —
  // підставляємо його автоматично (склад обов'язковий для товарних рядків).
  useEffect(() => {
    if (isPosted || form.warehouse_id) return;
    if (warehousesQ.data?.length === 1) {
      setForm((f) => f.warehouse_id ? f : { ...f, warehouse_id: warehousesQ.data![0]!.id });
    }
  }, [warehousesQ.data, isPosted, form.warehouse_id]);

  const invoiceFields = form.order_kind === 'PROFORMA_INVOICE';

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  function buildPayloadItems(): (CustomerOrderItem | SupplierOrderItem)[] {
    // Відкидаємо порожні рядки (без номенклатури або з нульовою кількістю):
    // на вкладці «Товари» порожній рядок не рендериться, але лишається у
    // стейті — без фільтра він пішов би у payload і впав 422.
    return form.items
      .filter((it) => (it.nomenclature_id || it.fixed_asset_id) && Number(it.quantity) > 0)
      .map((it, idx) => ({
      line_number: idx + 1,
      nomenclature_id: it.nomenclature_id || null,
      fixed_asset_id: it.fixed_asset_id || null,
      quantity: it.quantity,
      price: it.price,
      // П2: знижка — лише замовлення покупця (supplier-схема поля не має).
      ...(isCustomer ? {
        discount_percent: it.discount_percent || '0',
        service_description: it.service_description?.trim() || null,
        // 0388: склад рядка (резерв по-складово; supplier-схема поля не має).
        warehouse_id: it.warehouse_id || null,
      } : {}),
      vat_rate_id: it.vat_rate_id,
      account_code: it.account_code || null,
      amount_without_vat: it.amount_without_vat,
      vat_amount: it.vat_amount,
      total_amount: it.total_amount,
    }));
  }

  // 2026-07-27 (рішення власника): два попередження при ПРОВЕДЕННІ рахунку.
  // a) товарні рядки без складу (рядок+шапка порожні) → резерв НЕ створиться;
  // b) резерв перекриває відкритий ПОПИТ інших замовлень (аналітика 4-х
  //    показників). Не блокування — confirm, рішення за бухгалтером.
  async function confirmReserveWarnings(): Promise<boolean> {
    if (!isCustomer || !invoiceFields) return true;
    const goodsRows = form.items.filter(
      (it) => it.nomenclature_id && Number(it.quantity) > 0 && !rowIsService(it));
    if (goodsRows.length === 0) return true;

    const noWh = goodsRows.filter((it) => !(it.warehouse_id || form.warehouse_id));
    if (noWh.length > 0) {
      const names = noWh.map((it) =>
        nomList.data?.items.find((n) => n.id === it.nomenclature_id)?.name
        ?? `рядок ${it.line_number}`).slice(0, 3).join(', ');
      const ok = await confirm({
        title: 'Резерв не буде створено',
        message: `Рядки без складу (${names}${noWh.length > 3 ? '…' : ''}): `
          + 'резерв на складі створюється лише для рядків зі складом '
          + '(у рядку або в шапці). Провести без резерву?',
        okLabel: 'провести без резерву',
        cancelLabel: 'скасувати',
      });
      if (!ok) return false;
    }

    // b) ч.918 (рішення власника): рахунок можна виставити на БІЛЬШЕ, ніж є на
    //    складі — товар може бути в дорозі від постачальника. Баланс «можна
    //    продати» = склад + в дорозі − резерв інших − замовлено іншими.
    //    Не блокування — попередження; резерв створюється на вільну кількість,
    //    решта чекає надходження. (Замінило перевірку «резерв перекриває попит».)
    if (form.organization_id) {
      try {
        const qtyByNom = new Map<string, number>();
        for (const it of goodsRows) {
          qtyByNom.set(it.nomenclature_id,
            (qtyByNom.get(it.nomenclature_id) ?? 0) + Number(it.quantity));
        }
        const res = await reports.sellableBalance({
          organization_id: form.organization_id,
          on_date: form.document_date,
          nomenclature_ids: [...qtyByNom.keys()].join(','),
          exclude_order_id: isNew ? undefined : id,
        });
        const fmtQ = (n: number) => n.toLocaleString('uk-UA', { maximumFractionDigits: 3 });
        const lines: string[] = [];
        for (const r of res.rows) {
          const need = qtyByNom.get(r.nomenclature_id) ?? 0;
          const freeNow = Math.max(Number(r.free_now), 0);
          if (need <= freeNow) continue;
          const nm = nomList.data?.items.find((n) => n.id === r.nomenclature_id)?.name ?? 'товар';
          const sellable = Number(r.sellable);
          const parts = `на складі ${fmtQ(Number(r.stock))}, у резерві інших ${fmtQ(Number(r.reserved))}, `
            + `замовлено іншими ${fmtQ(Number(r.ordered))}, в дорозі ${fmtQ(Number(r.in_transit))}`;
          lines.push(need <= sellable
            ? `«${nm}»: потрібно ${fmtQ(need)}, вільно зараз ${fmtQ(freeNow)} — решта ${fmtQ(need - freeNow)} очікується з дороги (${parts}).`
            : `«${nm}»: потрібно ${fmtQ(need)}, а з урахуванням товару в дорозі доступно лише ${fmtQ(Math.max(sellable, 0))} — НЕ ВИСТАЧАЄ ${fmtQ(need - Math.max(sellable, 0))} (${parts}).`);
        }
        if (lines.length > 0) {
          const ok = await confirm({
            title: 'Кількість перевищує вільний залишок',
            message: `${lines.slice(0, 5).join('\n')}${lines.length > 5 ? `\n… і ще ${lines.length - 5}` : ''}\n\n`
              + 'Резерв буде створено лише на вільну кількість; відвантажити решту '
              + 'можна після надходження товару. Провести рахунок?',
            okLabel: 'провести',
            cancelLabel: 'скасувати',
          });
          if (!ok) return false;
        }
      } catch { /* аналітика недоступна — не блокуємо */ }
    }
    return true;
  }

  // 0389: «Резерв до» = планова дата оплати + 1 календарний день (авто);
  // порожня оплата → порожньо. Хрестик очищає вручну.
  useEffect(() => {
    // Лише НОВИЙ документ: у збереженому авто-перерахунок перетирав би
    // руками очищене/змінене значення при завантаженні.
    if (!invoiceFields || !isNew) return;
    if (form.payment_due_date) {
      const d = new Date(form.payment_due_date);
      d.setDate(d.getDate() + 1);
      const iso = d.toISOString().slice(0, 10);
      setForm((f) => f.reserve_until === iso ? f : { ...f, reserve_until: iso });
    } else {
      setForm((f) => f.reserve_until ? { ...f, reserve_until: '' } : f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.payment_due_date, invoiceFields]);

  // 0381 (рішення власника): термін поставки у двох видах — конкретна дата
  // АБО «N календарних днів з моменту оплати». У режимі днів delivery_date
  // до оплати порожня (показуємо орієнтовну оцінку), після досягнення порогу
  // оплати backend фіксує факт (delivery_date_fixed) — дата оплати + N.
  function deliveryTermsField(syncOnDelivery: boolean) {
    const mode: 'date' | 'days' = form.delivery_days ? 'days' : 'date';
    const fixed = !isNew && (docQuery.data?.delivery_date_fixed ?? false);
    // 2026-07-26 (уточнення власника): планова дата ПОРЯД з полем днів;
    // при ЧАСТКОВІЙ передоплаті строк рахується від планової дати
    // ПЕРЕДПЛАТИ (prepayment_due_date), не фінальної оплати.
    const estimate = (() => {
      if (mode !== 'days' || fixed) return null;
      const base = (form.payment_terms_type === 'PARTIAL'
        ? form.prepayment_due_date : form.payment_due_date)
        || form.payment_due_date || form.document_date;
      if (!base) return null;
      const dt = new Date(base);
      dt.setDate(dt.getDate() + Number(form.delivery_days));
      return dt.toLocaleDateString('uk-UA');
    })();
    return (
      <div className="field" style={{ gridColumn: 'span 3' }}>
        <label>Термін поставки</label>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <select
            value={mode}
            onChange={(e) => setForm(e.target.value === 'days'
              ? { ...form, delivery_days: form.delivery_days || '7', delivery_date: '' }
              : { ...form, delivery_days: '' })}
            disabled={isPosted}
            style={{ width: 130, flex: '0 0 auto' }}
          >
            <option value="date">дата</option>
            <option value="days">днів з оплати</option>
          </select>
          {mode === 'date' ? (
            <DatePopover value={form.delivery_date}
              onChange={(v) => setForm({ ...form, delivery_date: v,
                payment_due_date: syncOnDelivery && form.payment_terms_type === 'ON_DELIVERY'
                  ? v : form.payment_due_date })}
              disabled={isPosted} />
          ) : (
            <>
              <input type="number" min={0} value={form.delivery_days}
                onChange={(e) => setForm({ ...form, delivery_days: e.target.value })}
                disabled={isPosted} style={{ width: 80 }} />
              {fixed && form.delivery_date ? (
                <span style={{ color: 'var(--badge-green-fg)', whiteSpace: 'nowrap' }}
                      title="Зафіксовано фактом оплати">
                  {new Date(form.delivery_date).toLocaleDateString('uk-UA')}
                </span>
              ) : estimate && (
                <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}
                      title={form.payment_terms_type === 'PARTIAL'
                        ? 'Планова дата: від планової дати передоплати. Зафіксується фактом оплати частки.'
                        : 'Планова дата: від планової дати оплати. Зафіксується фактом оплати.'}>
                  {estimate}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Валідація перед збереженням: хоча б один заповнений рядок.
  function ensureHasItems(): boolean {
    if (buildPayloadItems().length === 0) {
      setError('Додайте хоча б один товар або послугу.');
      return false;
    }
    return true;
  }

  // 2026-07-25 (рішення власника): умови оплати ДОГОВОРУ підставляються у
  // рахунок при виборі договору (з ручним редагуванням). Планова дата
  // оплати: POSTPAY/PARTIAL → дата документа + відстрочка (payment_term_days
  // договору); ON_DELIVERY → термін поставки.
  function termsFromContract(cid: string, f: FormState): Partial<FormState> {
    if (!invoiceFields || !cid) return {};
    const c = (contractsQ.data ?? []).find((x) => x.id === cid);
    if (!c?.payment_terms_type) return {};
    const upd: Partial<FormState> = {
      payment_terms_type: c.payment_terms_type,
      prepayment_percent: c.payment_terms_type === 'PARTIAL' && c.prepayment_percent != null
        ? String(Number(c.prepayment_percent)) : '',
    };
    if ((c.payment_terms_type === 'POSTPAY' || c.payment_terms_type === 'PARTIAL')
        && c.payment_term_days != null) {
      const base = f.document_date ? new Date(f.document_date) : new Date();
      base.setDate(base.getDate() + Number(c.payment_term_days));
      upd.payment_due_date = base.toISOString().slice(0, 10);
    }
    if (c.payment_terms_type === 'ON_DELIVERY') {
      upd.payment_due_date = f.delivery_date || '';
    }
    return upd;
  }

  // Галки контролю дебіторки договору (debt_control_amount / debt_control_days):
  // попередження при збереженні рахунку, НЕ блокування.
  async function confirmDebtControl(): Promise<boolean> {
    if (!isCustomer || !invoiceFields || !form.contract_id) return true;
    try {
      const info = await refs.contractDebtInfo(form.contract_id);
      const warnings: string[] = [];
      if (info.debt_control_amount && info.debt_limit != null) {
        const debt = Number(info.debt_amount);
        const projected = debt + totalAmount;
        if (projected > Number(info.debt_limit)) {
          warnings.push(
            `поточний борг ${fmt(debt)} + цей рахунок ${fmt(totalAmount)} = `
            + `${fmt(projected)} грн — перевищує ліміт боргу за договором `
            + `${fmt(Number(info.debt_limit))} грн`);
        }
      }
      if (info.debt_control_days && info.max_overdue_days > 0) {
        warnings.push(
          `за договором є прострочена заборгованість (${info.max_overdue_days} дн.)`);
      }
      if (warnings.length === 0) return true;
      return await confirm({
        title: 'Контроль заборгованості за договором',
        message: `Увага: ${warnings.join('; ')}. Продовжити?`,
        okLabel: 'зберегти попри це',
        cancelLabel: 'скасувати',
      });
    } catch {
      return true; // контроль недоступний — не блокуємо збереження
    }
  }

  const create = useMutation({
    mutationFn: () => api.create({
      organization_id: form.organization_id,
      // Auto-numbering: порожнє → backend згенерує через next_number().
      document_number: form.document_number || null,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,
      delivery_date: form.delivery_date || null,
      delivery_days: form.delivery_days ? Number(form.delivery_days) : null,
      prepayment_due_date: form.prepayment_due_date || null,
      reserve_until: form.reserve_until || null,
      comment: form.comment || null,
      project_id: form.project_id,
      order_kind: form.order_kind,
      ...form.vat_flags,
      // ПРОДАЖ-розбивка Етап 1.
      incoterms: form.incoterms || null,
      payment_terms_type: form.payment_terms_type || null,
      prepayment_percent: form.prepayment_percent || null,
      final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
      // Порція А (0379): поля УТП.
      payment_due_date: form.payment_due_date || null,
      ...(isCustomer ? {
        bank_account_id: form.bank_account_id || null,
        base_order_id: form.base_order_id || null,
        delivery_address: form.delivery_address || null,
        consignee_name: form.consignee_name || null,
      } : {}),
      items: buildPayloadItems() as never,
    } as never),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: [queryKeyList] });
      void linkSource(doc.id);
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => api.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,
      delivery_date: form.delivery_date || null,
      delivery_days: form.delivery_days ? Number(form.delivery_days) : null,
      prepayment_due_date: form.prepayment_due_date || null,
      reserve_until: form.reserve_until || null,
      comment: form.comment || null,
      project_id: form.project_id,
      order_kind: form.order_kind,
      ...form.vat_flags,
      // ПРОДАЖ-розбивка Етап 1.
      incoterms: form.incoterms || null,
      payment_terms_type: form.payment_terms_type || null,
      prepayment_percent: form.prepayment_percent || null,
      final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
      // Порція А (0379): поля УТП.
      payment_due_date: form.payment_due_date || null,
      ...(isCustomer ? {
        bank_account_id: form.bank_account_id || null,
        base_order_id: form.base_order_id || null,
        delivery_address: form.delivery_address || null,
        consignee_name: form.consignee_name || null,
      } : {}),
      items: buildPayloadItems() as never,
    } as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: [queryKeySingle, id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await api.create({
        organization_id: form.organization_id,
        // Auto-numbering: порожнє → backend згенерує через next_number().
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        warehouse_id: form.warehouse_id || null,
        delivery_date: form.delivery_date || null,
        delivery_days: form.delivery_days ? Number(form.delivery_days) : null,
        prepayment_due_date: form.prepayment_due_date || null,
      reserve_until: form.reserve_until || null,
        comment: form.comment || null,
        project_id: form.project_id,
        order_kind: form.order_kind,
        ...form.vat_flags,
        // ПРОДАЖ-розбивка Етап 1.
        incoterms: form.incoterms || null,
        payment_terms_type: form.payment_terms_type || null,
        prepayment_percent: form.prepayment_percent || null,
        final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
      // Порція А (0379): поля УТП.
      payment_due_date: form.payment_due_date || null,
      ...(isCustomer ? {
        bank_account_id: form.bank_account_id || null,
        base_order_id: form.base_order_id || null,
        delivery_address: form.delivery_address || null,
        consignee_name: form.consignee_name || null,
      } : {}),
        items: buildPayloadItems() as never,
      } as never);
      try {
        await api.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`${baseRoute}/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: [queryKeyList] });
      void linkSource(doc.id);
      navigate(`${baseRoute}/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await api.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        contractor_id: form.contractor_id,
        contract_id: form.contract_id || null,
        warehouse_id: form.warehouse_id || null,
        delivery_date: form.delivery_date || null,
        delivery_days: form.delivery_days ? Number(form.delivery_days) : null,
        prepayment_due_date: form.prepayment_due_date || null,
      reserve_until: form.reserve_until || null,
        comment: form.comment || null,
        project_id: form.project_id,
        order_kind: form.order_kind,
        ...form.vat_flags,
        // ПРОДАЖ-розбивка Етап 1.
        incoterms: form.incoterms || null,
        payment_terms_type: form.payment_terms_type || null,
        prepayment_percent: form.prepayment_percent || null,
        final_payment_days: form.final_payment_days ? Number(form.final_payment_days) : null,
      // Порція А (0379): поля УТП.
      payment_due_date: form.payment_due_date || null,
      ...(isCustomer ? {
        bank_account_id: form.bank_account_id || null,
        base_order_id: form.base_order_id || null,
        delivery_address: form.delivery_address || null,
        consignee_name: form.consignee_name || null,
      } : {}),
        items: buildPayloadItems() as never,
      } as never);
      await api.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => api.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeyList] });
      navigate(baseRoute);
    },
  });
  // ПРОДАЖ-розбивка Етап 5 (2026-06-03): закриття/відкриття замовлення.
  const close = useMutation({
    mutationFn: (reason: string | undefined) => api.close(id!, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const reopen = useMutation({
    mutationFn: () => api.reopen(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKeySingle, id] });
      qc.invalidateQueries({ queryKey: [queryKeyList] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const closingStatus = docQuery.data?.closing_status ?? 'OPEN';
  const isClosed = closingStatus === 'CLOSED';

  function setItem(i: number, patch: Partial<ItemRow>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch
            || 'discount_percent' in patch || 'vat_amount' in patch) {
          // П2: знижка рядка — сума без ПДВ ПІСЛЯ знижки (як в УТП).
          // ч.1374: ціни з ПДВ / без, ПДВ вручну — за ознаками документа.
          const a = lineAmounts(Number(next.quantity), Number(next.price), rateOf(next.vat_rate_id), f.vat_flags, {
            discountPercent: Number(next.discount_percent) || 0,
            manualVat: 'vat_amount' in patch ? Number(next.vat_amount) : null,
          });
          next.amount_without_vat = a.amount_without_vat;
          next.vat_amount = a.vat_amount;
          next.total_amount = a.total_amount;
        }
        return next;
      }),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  const progressColumn = isCustomer ? 'quantity_shipped' : 'quantity_received';
  const progressLabel = isCustomer ? 'Відвантажено' : 'Отримано';

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    const what = isCustomer ? 'замовлення покупця' : 'замовлення постачальнику';
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>Нове {what}</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати {what} у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate(baseRoute)}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.849: «ще дії» → права колонка «Повʼязані дії».
  const railActions = [
              ...(!isClosed ? [{
                label: 'закрити замовлення',
                title: isNew ? 'Спершу збережіть документ'
                  : 'Закрити замовлення вручну (наприклад, клієнт відмовився)',
                disabled: close.isPending || isNew,
                onClick: () => {
                  const reason = window.prompt('Причина закриття (необов\'язково):', '');
                  if (reason !== null) close.mutate(reason || undefined);
                },
              }] : [{
                label: 'відкрити замовлення', title: 'Відкрити назад',
                disabled: reopen.isPending, onClick: () => reopen.mutate(),
              }]),
              ...(form.order_kind === 'ORDER' ? [{
                label: 'створити рахунок',
                title: isNew ? 'Спершу збережіть документ'
                  : 'Створити рахунок на підставі цього замовлення (копія рядків)',
                disabled: isNew,
                onClick: () => navigate(`${isCustomer ? '/customer-invoices' : '/supplier-invoices'}/new?copy_from=${id}`),
              }] : []),
              // ч.1359 (обернений ланцюг): рахунок виписали раніше — замовлення з нього.
              ...(form.order_kind === 'PROFORMA_INVOICE' ? [{
                label: isCustomer ? 'створити замовлення покупця' : 'створити замовлення постачальнику',
                title: isNew ? 'Спершу збережіть документ'
                  : 'Замовлення з рядків цього рахунку; документи будуть у «Звʼязаних»',
                disabled: isNew,
                onClick: () => navigate(`${isCustomer ? '/customer-orders' : '/supplier-orders'}/new?from_invoice=${id}`),
              }] : []),
              // ч.740: постачальник — платіжка / РКО на суму замовлення/рахунку.
              ...(!isCustomer ? moneyDocActions({ navigate, isNew, direction: 'out', prefill: {
                org: form.organization_id, contractor: form.contractor_id, contract: form.contract_id,
                order: isNew ? null : id, project: form.project_id, op: 'TO_SUPPLIER',
                amount: totalWithoutVat + totalVat,
                purpose: `Оплата згідно з ${form.order_kind === 'PROFORMA_INVOICE' ? 'рахунком' : 'замовленням'} № ${form.document_number} від ${fmtDocDate(form.document_date)}${vatTail(totalVat)}`,
              } }) : []),
              ...(isCustomer ? [{
                // ч.1347: «на підставі» — зі збереженого документа, проведення не чекаємо.
                label: 'створити документ реалізації',
                title: isNew ? 'Спершу збережіть документ'
                  : 'Створити документ реалізації (товари і послуги) на підставі: рядки заповняться залишком до відвантаження',
                disabled: isNew,
                onClick: () => navigate(`/goods-sales/new?basis=${id}`),
              }, {
                label: 'створити акт виробничих послуг',
                title: isNew ? 'Спершу збережіть документ'
                  : 'Створити акт виробничих послуг на підставі: рядки-послуги цього рахунку',
                disabled: isNew,
                onClick: () => navigate(`/production-service-acts/new?basis=${id}`),
              }] : [{
                label: 'створити надходження',
                title: isNew ? 'Спершу збережіть документ'
                  : 'Створити надходження товарів на підставі: рядки заповняться залишком до отримання',
                disabled: isNew,
                onClick: () => navigate(`/goods-receipts/new?basis=${id}`),
              }]),
  ];

  // ч.849: прогрес рахує ЛИШЕ ОБОВʼЯЗКОВІ поля (рішення власника).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: `${partyLabel} і договір`, required: 1,
      filled: form.contractor_id ? 1 : 0 },
    ...(invoiceFields ? [{ id: 'terms', title: 'Оплата та умови', required: 0, filled: 0 }] : []),
    { id: 'items', title: 'Товари / Послуги', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.749: канон — інфо-ряд дт-кт/історія/звʼязані у рядку назви документа. */}
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          {isNew
            ? (form.order_kind === 'PROFORMA_INVOICE'
                ? (isCustomer ? 'Новий рахунок на оплату' : 'Новий рахунок від постачальника')
                : (isCustomer ? 'Нове замовлення покупця' : 'Нове замовлення постачальнику'))
            : `${form.order_kind === 'PROFORMA_INVOICE'
                ? (isCustomer ? 'Рахунок на оплату' : 'Рахунок від постачальника')
                : (isCustomer ? 'Замовлення покупця' : 'Замовлення постачальнику')}: ${form.document_number}`}
          {/* П4a: сплачено за замовленням (Σ оплат з deal=замовлення). */}
          {!isNew && docQuery.data?.paid_amount != null && Number(docQuery.data.paid_amount) > 0 && (
            <span style={{
              marginLeft: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: 4,
              fontSize: '0.85rem', fontWeight: 600,
              background: Number(docQuery.data.paid_amount) >= totalAmount - 0.01 ? '#dcfce7' : '#fef3c7',
              color: Number(docQuery.data.paid_amount) >= totalAmount - 0.01 ? '#15803d' : '#b45309',
            }}
              title="Сума оплат, зарахованих за цим замовленням (поле «На підставі» у платіжці/ПКО)">
              оплачено {fmt(Number(docQuery.data.paid_amount))} з {fmt(totalAmount)}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            isNew={isNew}
            isPosted={isPosted}
            onBackToList={() => navigate(baseRoute)}
            onSave={() => {
              if (!ensureHasItems()) return;
              void confirmDebtControl().then((ok) => {
                if (ok) (isNew ? create.mutate() : update.mutate());
              });
            }}
            saveDisabled={isPosted || !form.organization_id || !form.contractor_id}
            savePending={create.isPending || update.isPending}
            onPost={() => {
              if (!ensureHasItems()) return;
              void confirmDebtControl().then(async (ok) => {
                if (!ok) return;
                if (!(await confirmReserveWarnings())) return;
                (isNew ? createAndPost.mutate() : updateAndPost.mutate());
              });
            }}
            onUnpost={() => unpost.mutate()}
            postDisabled={!form.organization_id || !form.contractor_id || form.items.length === 0}
            postPending={createAndPost.isPending || updateAndPost.isPending}
            unpostPending={unpost.isPending}
            postTitle="Зберігає та проводить документ"
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
            deleteDisabled={isPosted}
            deletePending={remove.isPending}
            onPrint={(!isNew && isCustomer && form.order_kind === 'PROFORMA_INVOICE')
              ? () => navigate(`/customer-invoices/${id}/print`)
              : (!isNew && !isCustomer && form.order_kind === 'ORDER')
                ? () => navigate(`/supplier-orders/${id}/print`)
                : undefined}
            entriesDoc={{ type: isCustomer ? 'doc.customer_orders' : 'doc.supplier_orders', id: isNew ? '' : id! }}
            extraStatusBadges={!isNew ? [isClosed
              ? { label: `закрито${docQuery.data?.closed_at ? ' ' + new Date(docQuery.data.closed_at).toLocaleDateString('uk-UA') : ''}`,
                  tone: 'blue' as const, title: docQuery.data?.close_reason ?? undefined }
              : { label: 'відкрито', tone: 'amber' as const,
                  title: 'Замовлення відкрите — очікує виконання/закриття' }] : []}
            // ч.849: доп-дії переїхали у праву колонку («Повʼязані дії»).
            railMode
            extraActions={railActions}
          />
        </div>
      </div>

      {/* ч.849 (новий дизайн): ліворуч — пронумеровані блоки полів,
          праворуч — рейка (заповнення / повʼязані дії / історія / звʼязані). */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`${baseRoute}/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: isCustomer ? 'doc.customer_orders' : 'doc.supplier_orders',
                 id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
            style={{
              borderColor: isNew && !form.organization_id ? '#d33' : undefined,
            }}
          >
            <option value="">— оберіть юрособу —</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && !form.organization_id && !permsLoading && (
            <small style={{ color: '#d33', fontSize: '0.75rem' }}>
              {availableOrgs.length === 0 && !hasPermissionInAnyOrg
                ? `У вас немає права «${requiredPerm}» у жодній юрособі`
                : 'Спочатку оберіть юрособу'}
            </small>
          )}
          {isNew && availableOrgs.length < totalAccessibleOrgs && availableOrgs.length > 0 && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            disabled={isPosted}
            placeholder={
              isNew
                ? (form.order_kind === 'PROFORMA_INVOICE'
                    ? (isCustomer ? 'РО-0001-2026 (auto)' : 'РП-0001-2026 (auto)')
                    : (isCustomer ? 'ЗП-0001-2026 (auto)' : 'ЗС-0001-2026 (auto)'))
                : undefined
            }
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        {/* 2026-08-07 (рішення власника): у ЗАМОВЛЕННІ «Напрям» — у рядку
            дати; у РАХУНКУ ПОКУПЦЮ — поруч з банкрахунком (нижче).
            ч.743: у рахунку ПОСТАЧАЛЬНИКА — одразу після Дати (тут). */}
        {(!invoiceFields || !isCustomer) && (
          <ProjectField span={4}
              value={form.project_id}
              organizationId={form.organization_id}
              onChange={(v) => setForm({ ...form, project_id: v })}
              disabled={isPosted} />
        )}
        {isCustomer && invoiceFields && (
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Банкрахунок</label>
            <select value={form.bank_account_id}
                    onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">— основний —</option>
              {bankAccountsQ.data?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.iban}{b.account_name ? ` — ${b.account_name}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {invoiceFields && isCustomer && (
          <ProjectField span={2}
              value={form.project_id}
              organizationId={form.organization_id}
              onChange={(v) => setForm({ ...form, project_id: v })}
              disabled={isPosted} />
        )}
      </div>

      </DocBlock>

      <DocBlock index={2} title={`${partyLabel} і договір`}>
      {/* 2026-07-25 (рішення власника): рядок 2 — Покупець | Договір |
          «створити з замовлення»; рядок 3 — Умови оплати + залежні поля;
          рядок 4 — Термін поставки | Інкотермс | Адреса | Вантажоодержувач. */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{partyLabel} *
            <QuickCreateLink title="Створити нового контрагента"
              disabled={isPosted}
              onClick={() => setQuickContractor(true)} />
          </label>
          <SearchSelect
            items={(filteredContractors).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            // 2026-07-25: на РАХУНКУ зміна покупця/договору очищає дату
            // поставки і табличну частину (вони могли бути з чужого замовлення).
            // 2026-07-31: поки є підстава-замовлення — поле неактивне (✕ знімає).
            onChange={(cid) => setForm({ ...form, contractor_id: cid, contract_id: '',
              ...(invoiceFields ? { delivery_date: '', base_order_id: '', items: [] } : {}) })}
            disabled={isPosted || (invoiceFields && !!form.base_order_id)}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>
            Договір{' '}
            <small style={{ color: 'var(--muted)' }}>(авто вкл.)</small>
            <QuickCreateLink
              disabled={isPosted || !form.contractor_id}
              title={form.contractor_id ? 'Створити договір цього контрагента' : 'Спершу оберіть контрагента'}
              onClick={() => setQuickContract(true)} />
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value,
                    ...(invoiceFields ? { delivery_date: '', base_order_id: '', items: [] } : {}),
                    // Умови оплати договору → у рахунок (після очистки,
                    // щоб ON_DELIVERY бачив уже порожній термін поставки).
                    ...termsFromContract(e.target.value,
                      { ...form, delivery_date: invoiceFields ? '' : form.delivery_date }) })}
                  disabled={isPosted || !form.contractor_id
                    || (invoiceFields && !!form.base_order_id)}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        {invoiceFields && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>&nbsp;</label>
            {/* 2026-07-31 (рішення власника): підстава — одразу за кнопкою;
                звʼязок блокує Покупець/Договір/кнопку; ✕ знімає звʼязок і
                очищає табличну частину. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button type="button"
                onClick={() => setOrdersPickerOpen(true)}
                disabled={isPosted || !form.contractor_id || !form.contract_id
                  || !!form.base_order_id}
                title={form.base_order_id
                  ? 'Рахунок вже заповнено з замовлення — зніміть звʼязок (✕), щоб обрати інше'
                  : !form.contractor_id || !form.contract_id
                    ? `Спершу оберіть ${isCustomer ? 'покупця' : 'постачальника'} і договір`
                    : `Заповнити рахунок з активного замовлення ${isCustomer ? 'покупця' : 'постачальнику'}`}
                className="btn-doc">
                <DocIcon />створити з замовлення
              </button>
              {form.base_order_id && (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  Підстава: {' '}
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => navigate(`${isCustomer ? '/customer-orders' : '/supplier-orders'}/${form.base_order_id}`)}>
                    замовлення {basisOrderQ.data
                      ? `№${basisOrderQ.data.document_number} від ${new Date(basisOrderQ.data.document_date).toLocaleDateString('uk-UA')}`
                      : ''}
                  </a>
                  {!isPosted && (
                    <button type="button" style={{ marginLeft: 6, padding: '0 0.4rem' }}
                      onClick={() => setForm({ ...form, base_order_id: '', items: [] })}
                      title="Зняти звʼязок з замовленням і очистити рядки">×</button>
                  )}
                </span>
              )}
            </div>
          </div>
        )}
        {!invoiceFields && deliveryTermsField(false)}
        {/* 2026-08-07 (рішення власника): склад ПІДНЯТО з рядка вкладок. */}
        {!invoiceFields && isCustomer && (
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Склад за замовчуванням</label>
            <select value={form.warehouse_id}
                    onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">—</option>
              {warehousesQ.data?.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      </DocBlock>

      {invoiceFields && (
      <DocBlock index={3} title="Оплата та умови">
      <>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Умови оплати</label>
          <select
            value={form.payment_terms_type}
            onChange={(e) => {
              const ptt = e.target.value as FormState['payment_terms_type'];
              setForm({
                ...form,
                payment_terms_type: ptt,
                prepayment_percent: ptt === 'PARTIAL' ? form.prepayment_percent : '',
                // «При отриманні» — планової дати оплати немає як такої,
                // беремо термін поставки (рішення власника 2026-07-25).
                payment_due_date: ptt === 'ON_DELIVERY'
                  ? (form.delivery_date || form.payment_due_date)
                  : form.payment_due_date,
              });
            }}
            disabled={isPosted}
          >
            <option value="">— Не вказано —</option>
            {(Object.keys(PAYMENT_TERMS_LABELS) as Array<keyof typeof PAYMENT_TERMS_LABELS>).map((pt) => (
              <option key={pt} value={pt}>{PAYMENT_TERMS_LABELS[pt]}</option>
            ))}
          </select>
        </div>
        {form.payment_terms_type === 'PARTIAL' && (
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Розмір передоплати, % <span style={{ color: 'red' }}>*</span></label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="99.99"
              value={form.prepayment_percent}
              onChange={(e) => setForm({ ...form, prepayment_percent: e.target.value })}
              disabled={isPosted}
              placeholder="наприклад 30"
            />
          </div>
        )}
        {form.payment_terms_type === 'PARTIAL' && (
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Передплата до <small style={{ color: 'var(--muted)' }}>(планова дата)</small></label>
            <DatePopover value={form.prepayment_due_date}
                   onChange={(v) => setForm({ ...form, prepayment_due_date: v })}
                   disabled={isPosted} />
          </div>
        )}
        {/* 2026-07-25 (рішення власника): поле «Строк кінцевого розрахунку,
            роб. днів» прибрано — уніфіковано з «Оплата (планова дата)»
            (значиме поле — саме дата). Колонка в БД лишається,
            розрахункової логіки на ній не було (просрочка в звіті розрахунків
            рахується від payment_term_days ДОГОВОРУ). */}
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Оплата <small style={{ color: 'var(--muted)' }}>(планова дата)</small></label>
          <DatePopover value={form.payment_due_date}
                 onChange={(v) => setForm({ ...form, payment_due_date: v })}
                 disabled={isPosted} />
        </div>
        {/* 2026-07-31 (рішення власника): «Резерв до» — лише рахунок
            ПОКУПЦЮ (резерв складу); у постачальницькому не показуємо. */}
        {isCustomer && (
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Резерв до{' '}
            <small style={{ color: 'var(--muted)' }}>(авто: оплата + 1 день)</small>
          </label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <DatePopover value={form.reserve_until}
                   onChange={(v) => setForm({ ...form, reserve_until: v })}
                   disabled={isPosted} />
            {form.reserve_until && !isPosted && (
              <button type="button" style={{ padding: '0 0.45rem' }}
                onClick={() => setForm({ ...form, reserve_until: '' })}
                title="Очистити «Резерв до»">×</button>
            )}
          </div>
        </div>
        )}
      </div>
      {/* Рядок 4: Термін поставки | Інкотермс | Склад | Пункт постачання.
          2026-08-07 (рішення власника): склад ПІДНЯТО сюди, поля статичні. */}
      <div className="doc-grid">
        {deliveryTermsField(true)}
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Умови постачання (Інкотермс)</label>
          <select
            value={form.incoterms}
            onChange={(e) => setForm({ ...form, incoterms: e.target.value })}
            disabled={isPosted}
          >
            <option value="">— Не вказано —</option>
            {INCOTERMS_OPTIONS.map((it) => (
              <option key={it} value={it}>{it}</option>
            ))}
          </select>
        </div>
        {/* 2026-07-27 (рішення власника): транспортні реквізити переїхали у
            ТТН. Замість «Адреса доставки»/«Вантажоодержувач» — «Пункт
            постачання» ЗА ІНКОТЕРМС (те саме поле delivery_address у БД):
            видиме для термінів з обов'язком доставки; EXW = самовивіз зі
            складу продавця — пункт не потрібен. */}
        {/* 2026-08-07: «Пункт постачання» ОДРАЗУ за Інкотермсом. */}
        {isCustomer && form.incoterms && form.incoterms !== 'EXW' && (
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Пункт постачання{' '}
              <small style={{ color: 'var(--muted)' }}>({form.incoterms})</small>
            </label>
            <input value={form.delivery_address}
                   onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                   disabled={isPosted} maxLength={500}
                   placeholder="місто, адреса за умовами постачання" />
          </div>
        )}
        {/* Склад відвантаження — після пункту постачання (лише рахунок). */}
        {isCustomer && invoiceFields && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Склад за замовчуванням</label>
            <select value={form.warehouse_id}
                    onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">—</option>
              {warehousesQ.data?.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      </>
      </DocBlock>
      )}

      <DocBlock index={invoiceFields ? 4 : 3} title="Товари / Послуги"
        actions={!isPosted ? (linesTab === 'assets' ? (
          <button className="primary" onClick={() => setFaPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть юрособу' : 'Підбір ОЗ в експлуатації'}>
            + вибрати ОЗ
          </button>
        ) : (<>
          <VatFlagsButton value={form.vat_flags} onChange={applyVatFlags}
            isVatPayer={docVatPayer} />
          <button onClick={() => setQuickNomOpen(true)} className="btn-doc"
            title="Створити нову номенклатуру (мінімум полів) і одразу підставити рядком">
            <DocIcon />створити номенклатуру
          </button>
          <button className="primary" onClick={() => setPickerOpen(true)}
            title={linesTab === 'services'
              ? 'Підбір послуги'
              : 'Підбір товару: фільтр за складом і видом, залишки'}>
            + додати товар
          </button>
        </>)) : undefined}>
      {/* Порція А: вкладки рядків «як в УТП» (Товари / Послуги) + склад відвантаження. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div className="seg-tabs">
          <button type="button" className={linesTab === 'goods' ? 'active' : ''}
            onClick={() => setLinesTab('goods')}>Товари ({goodsCount})</button>
          <button type="button" className={linesTab === 'services' ? 'active' : ''}
            onClick={() => setLinesTab('services')}>Послуги ({servicesCount})</button>
          <button type="button" className={linesTab === 'equipment' ? 'active' : ''}
            onClick={() => setLinesTab('equipment')}
            title="Необоротні активи (рахунок 152): при створенні надходження на підставі ляжуть на ту саму вкладку">
            {isCustomer ? 'Обладнання' : 'Необоротні активи'} ({equipCount})</button>
          {isCustomer && (
            <button type="button" className={linesTab === 'assets' ? 'active' : ''}
              onClick={() => setLinesTab('assets')}
              title="Продаж основних засобів в експлуатації: реалізація на підставі ляже на вкладку «ОЗ»">
              ОЗ ({faCount})</button>
          )}
        </div>
        {/* 2026-07-31: у рахунку ПОСТАЧАЛЬНИКА склад відвантаження не має
            сенсу (це поле продажу), «Напрям» переїхав у рядок умов
            постачання вище. */}
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>{linesTab === 'assets' ? 'Основний засіб' : 'Номенклатура'}</th>
            {linesTab === 'assets' && (
              <th style={{ width: 130, textAlign: 'right' }} title="Первісна − знос (з картки)">Залишкова</th>
            )}
            {linesTab === 'services' && isCustomer && (
              <th title="Текст у друковану форму рахунку">Зміст послуги</th>
            )}
            {linesTab === 'goods' && isCustomer && (
              <th style={{ width: 150 }}
                  title="Склад рядка (порожньо — склад з шапки): резерв по-складово">
                Склад
              </th>
            )}
            <th className="num" style={{ width: 90 }}>К-сть</th>
            <th className="num" style={{ width: 110 }}
                title={form.vat_flags.prices_include_vat ? 'Ціна рядка вже з ПДВ («сума включає ПДВ»)' : undefined}>
              {form.vat_flags.prices_include_vat ? 'Ціна з ПДВ' : 'Ціна'}
            </th>
            {isCustomer && (
              <th className="num" style={{ width: 80 }}
                  title="Знижка рядка, % (дефолт — з договору)">Знижка %</th>
            )}
            <th className="num" style={{ width: 130 }}>
              {showVatColumns ? 'Сума без ПДВ' : 'Сума'}
            </th>
            {showVatColumns && (
              <>
                <th style={{ width: 100 }}>ПДВ</th>
                <th className="num" style={{ width: 110 }}>ПДВ грн</th>
                <th className="num" style={{ width: 120 }}>Усього</th>
              </>
            )}
            {!isNew && <th className="num" style={{ width: 100 }}>{progressLabel}</th>}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ it, i }) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              {it.fixed_asset_id ? (<>
                <td>{faLabel(it.fixed_asset_id)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {fmt(Number(faById.get(it.fixed_asset_id)?.residual_value ?? 0))}
                </td>
              </>) : (
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  kind={linesTab === 'services' ? 'services' : 'goods'}
                  disabled={isPosted}
                  onChange={(nomId) => {
                    setItem(i, { nomenclature_id: nomId,
                      ...(showVatColumns && vatRateByNomId.has(nomId)
                        ? { vat_rate_id: vatRateByNomId.get(nomId)! } : {}) });
                    // П2: знижка з договору (лише покупець).
                    if (isCustomer && contractDiscount && !(Number(it.discount_percent) > 0)) {
                      setItem(i, { discount_percent: contractDiscount });
                    }
                    // П1: автопідстановка ціни за типом цін договору (як в УТП).
                    void priceFor(nomId).then((p) => {
                      if (p != null) setItem(i, { price: p });
                    });
                  }}
                />
              </td>
              )}
              {linesTab === 'services' && isCustomer && (
                <td>
                  <input value={it.service_description ?? ''}
                         onChange={(e) => setItem(i, { service_description: e.target.value })}
                         disabled={isPosted} maxLength={500}
                         placeholder="текст у рахунок" style={{ width: '100%' }} />
                </td>
              )}
              {linesTab === 'goods' && isCustomer && (
                <td>
                  <select value={it.warehouse_id ?? ''}
                          onChange={(e) => setItem(i, { warehouse_id: e.target.value || null })}
                          disabled={isPosted} style={{ width: '100%' }}>
                    <option value="">— за замовчуванням —</option>
                    {warehousesQ.data?.map((w) => (
                      <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                    ))}
                  </select>
                </td>
              )}
              <td>
                <input type="number" step="0.001" value={it.quantity}
                       onChange={(e) => setItem(i, { quantity: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td>
                <input type="number" step="0.01" value={it.price}
                       onChange={(e) => setItem(i, { price: e.target.value })}
                       disabled={isPosted} />
              </td>
              {isCustomer && (
                <td>
                  <input type="number" step="0.01" min="0" max="100"
                         value={it.discount_percent ?? '0'}
                         onChange={(e) => setItem(i, { discount_percent: e.target.value })}
                         disabled={isPosted} />
                </td>
              )}
              <td style={{ textAlign: 'right' }}>{fmt(Number(it.amount_without_vat))}</td>
              {showVatColumns && (
                <>
                  <td>
                    <select value={it.vat_rate_id}
                            onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                            disabled={isPosted}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!form.vat_flags.vat_auto && !isPosted ? (
                      <input type="number" step="0.01" min="0" value={it.vat_amount}
                             title="Сума ПДВ вручну (авторозрахунок ПДВ вимкнено)"
                             onChange={(e) => setItem(i, { vat_amount: e.target.value })} />
                    ) : fmt(Number(it.vat_amount))}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
                </>
              )}
              {!isNew && (
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {Number(it[progressColumn] ?? 0).toLocaleString('uk-UA',
                    { minimumFractionDigits: 0, maximumFractionDigits: 4 })}
                </td>
              )}
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {/* ч.750: «Разом» — вкладка, «Усього» — весь документ. */}
          <tr style={{ fontWeight: 500 }}>
            <td colSpan={(isCustomer ? 6 : 4)} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabWithoutVat)}</td>
            {showVatColumns && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(tabVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(tabWithoutVat + tabVat)}</td>
              </>
            )}
            {!isNew && <td></td>}
            <td></td>
          </tr>
          <tr>
            <td colSpan={(isCustomer ? 6 : 4)} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {showVatColumns && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            {!isNew && <td></td>}
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      {/* 2026-07-25 (рішення власника): «Сума До сплати» — під «Усього»
          табличної частини (обчислюється локально для UX; backend
          computed_field due_now_amount дасть те саме). */}
      {invoiceFields && form.payment_terms_type && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem',
          background: 'var(--bg-soft)',
          borderRadius: 'var(--radius)',
          fontSize: '0.9rem',
          textAlign: 'right',
        }}>
          <strong>Сума «До сплати»: </strong>
          {(() => {
            const total = totalAmount;
            if (form.payment_terms_type === 'PREPAY') return fmt(total) + ' (100%)';
            if (form.payment_terms_type === 'PARTIAL' && form.prepayment_percent) {
              const dueNow = total * Number(form.prepayment_percent) / 100;
              return `${fmt(dueNow)} (${form.prepayment_percent}% від ${fmt(total)})`;
            }
            if (form.payment_terms_type === 'POSTPAY') return '0.00 (постоплата)';
            if (form.payment_terms_type === 'ON_DELIVERY') return '0.00 (при отриманні)';
            return fmt(total);
          })()}
        </div>
      )}
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            setForm((f) => ({
              ...f,
              items: [...f.items, {
                ...newRow(f.items.length + 1,
                  nom.vat_rate_id ?? effectiveDefaultVatId),
                nomenclature_id: nom.id,
                is_service_hint: nom.is_service,
                account_code: linesTab === 'equipment' ? '152' : null,
                discount_percent: (isCustomer && contractDiscount) ? contractDiscount : '0',
              }],
            }));
            setLinesTab(nom.is_service ? 'services' : 'goods');
          }}
        />
      )}
      {ordersPickerOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setOrdersPickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Активні замовлення покупця</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setOrdersPickerOpen(false)}>закрити</button>
            </div>
            {activeOrdersQ.isLoading && <p>Завантаження…</p>}
            {!activeOrdersQ.isLoading && (activeOrdersQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>
                Немає активних (відкритих, проведених) замовлень цього покупця.
              </p>
            )}
            {(activeOrdersQ.data?.items.length ?? 0) > 0 && (
              <TchTable>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--muted)' }}>
                    <th style={{ textAlign: 'left' }}>№</th>
                    <th style={{ textAlign: 'left' }}>Дата</th>
                    <th style={{ textAlign: 'left' }}>Термін поставки</th>
                    <th style={{ textAlign: 'right' }}>Сума</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOrdersQ.data?.items.map((o) => (
                    <tr key={o.id}
                        onClick={() => void applyFromOrder(o.id)}
                        style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                      <td>{o.document_number}</td>
                      <td>
                        {o.document_date ? new Date(o.document_date).toLocaleDateString('uk-UA') : ''}
                      </td>
                      <td>
                        {o.delivery_date ? new Date(o.delivery_date).toLocaleDateString('uk-UA') : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {fmt(Number(o.total_amount))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )}
          </div>
        </div>
      )}
      {faPickerOpen && form.organization_id && (
        <FixedAssetPickerModal
          organizationId={form.organization_id}
          excludeIds={form.items.map((it) => it.fixed_asset_id ?? '').filter(Boolean)}
          onPick={(a) => {
            setForm((f) => ({
              ...f,
              items: [...f.items, {
                ...newRow(f.items.length + 1, effectiveDefaultVatId),
                fixed_asset_id: a.id, quantity: '1',
              }],
            }));
            setFaPickerOpen(false);
          }}
          onClose={() => setFaPickerOpen(false)}
        />
      )}
      {/* ч.920 (рішення власника): у рахунку/замовленні ПОКУПЦЯ товари підбираються
          окремим попапом — наявність (на складі / в дорозі / в резерві /
          замовлено / баланс), ціни, серверна пагінація і сортування. Інші
          документи і вкладка «Послуги» — спільний попап без змін. */}
      {pickerOpen && isCustomer && linesTab !== 'services' && (
        <NomenclatureAvailabilityPicker
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || undefined}
          // ч.923: клік по ціні → рядок з цією ціною; клік по рядку → без ціни
          // (автопідстановка ціни за договором у цьому попапі не спрацьовує).
          onPick={(row, price) => addRowFromPicker(row, { price: price ?? null })}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {pickerOpen && !(isCustomer && linesTab !== 'services') && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || undefined}
          catalogMode
          expandByWarehouse={linesTab !== 'services'}
          onlyKind={linesTab === 'services' ? 'services' : 'goods'}
          // «лише з залишком» — тільки для товарів (послуги не складські).
          defaultOnlyWithStock={isCustomer && linesTab !== 'services'}
          onPick={addRowFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      </DocBlock>

      <DocBlock index={invoiceFields ? 5 : 4} title="Коментарі та вкладення">
      {/* ч.850 (рішення власника): дві колонки — коментар | вкладення. */}
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={kind === 'customer' ? 'doc.customer_orders' : 'doc.supplier_orders'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          {isCustomer
            ? 'Замовлення покупця: бухгалтерських проводок не створює, лише записує до регістру замовлень для відстеження виконання. При відвантаженні з посиланням на це замовлення регістр автоматично закриється.'
            : 'Замовлення постачальнику: бухгалтерських проводок не створює. При надходженні товару з посиланням на це замовлення поле «Отримано» в рядках оновиться автоматично.'}
        </p>
      )}
      {/* ч.948: попапи швидкого створення просто з форми документа. */}
      {quickContractor && (
        <QuickContractorModal
          direction={kind === 'customer' ? 'IN' : 'OUT'}
          onClose={() => setQuickContractor(false)}
          onCreated={(cid) => {
            setQuickContractor(false);
            setForm((f) => ({ ...f, contractor_id: cid, contract_id: '' }));
            qc.invalidateQueries({ queryKey: ['contractors'] });
          }} />
      )}
      {quickContract && form.contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.contractor_id}
          contractorName={contractorNameForQuick}
          direction={kind === 'customer' ? 'IN' : 'OUT'}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.contractor_id] });
          }} />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: {
          detail?: string | Array<{ loc?: unknown[]; msg?: string }>
        }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    // Pydantic 422: detail — array з обʼєктів {type, loc, msg, input, ctx}
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1])
          : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
